import { ServerService, TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DeserializedServer as Server, EC2Server, InterpretedServer, OutputEc2, UOMS } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Container, Grid, Header, Link, NonCancelableCustomEvent, SpaceBetween, Spinner, Toggle, ToggleProps } from '@amzn/awsui-components-react';
import React, { useCallback, useEffect, useState } from 'react';

import { constants } from '../../common/Localization';
import { uomConversion } from '../../common/uomConversion';
import { AwsEC2RecommendationDetail } from './ApplicationResourceDetailsAwsEC2';
import { ValueWithLabel } from './utils';

type Props = {
    server: InterpretedServer<Server>;
    tcoAssessmentId?: string;
    portfolioId: string;
    onInfoLinkClick: (id: string) => void;
} & FlashContextInterface;

const tcoService = new TcoService();
const serverService = new ServerService();
const convertMiBToGiB = (value: number) => {
    return Math.round(uomConversion(value, UOMS.MB, UOMS.GB));
};
const convertMBToGB = (value: number) => value / 1000;
function ApplicationResourceServerDetails({ server, tcoAssessmentId, portfolioId, onInfoLinkClick, flash }: Props) {
    const { alert } = flash;
    const [loading, setLoading] = useState(false);
    const [serverResult, setServerResult] = useState<EC2Server | undefined>();
    const [awsEC2result, setAwsEC2result] = useState<OutputEc2 | undefined>();
    const [hasVMCRecommendation, setHasVMCRecommendation] = useState(false);
    const [vmcPaymentOption, setVmcPaymentOption] = useState('');
    const [vmcClusterName, setVmcClusterName] = useState('');
    const [vmcHostType, setVmcHostType] = useState('');
    const [vmcHostvCPU, setVmcHostvCPU] = useState<number | null>(null);
    const [vmcHostRAM, setVmcHostRAM] = useState<number | null>(null);
    const [vmcHostStorage, setVmcHostStorage] = useState<number | null>(null);

    const loadData = useCallback( async () => {
        if (!tcoAssessmentId) {
            return;
        }

        setLoading(true);
        try {
            const awsEC2result: OutputEc2 = (await tcoService.getAwsEC2(tcoAssessmentId)).output?.aws?.ec2;
            if (!awsEC2result) {
                return;
            }
            // check for VMC Recommendation if virtual
            if (!server.interpretedFields?.isPhysical.value && awsEC2result.vmwareSizeAssessment && awsEC2result.vmwareCostAssessment) {
                const vmwareServer = awsEC2result.vmwareSizeAssessment.servers.find(ec2 => ec2.id === server.serverId);
                if (vmwareServer) {
                    setHasVMCRecommendation(true);
                    const vmwareCostCluster = awsEC2result.vmwareCostAssessment.clusters.find(
                        cluster => cluster.clusterName === vmwareServer.clusterName) || awsEC2result.vmwareCostAssessment.clusters[0];
                    const vmwareSizeCluster = awsEC2result.vmwareSizeAssessment.clusters.find(
                        cluster => cluster.name === vmwareServer.clusterName) || awsEC2result.vmwareSizeAssessment.clusters[0];
                    const paymentOption = vmwareCostCluster.cost.recommended === 'onDemand' ? constants.ON_DEMAND :
                        `${vmwareCostCluster.cost.recommended[0]}${constants.YEAR_SUBSCRIPTION}`;
                    setVmcPaymentOption(paymentOption);
                    setVmcClusterName(vmwareSizeCluster.name || '');
                    setVmcHostType(vmwareSizeCluster.hostInstanceType || '');

                    if (!vmwareSizeCluster.hostCount) {
                        vmwareSizeCluster.hostCount = 1;
                    }

                    setVmcHostvCPU(vmwareSizeCluster.compute.totalLogicalCPUs / vmwareSizeCluster.hostCount);
                    setVmcHostRAM(convertMiBToGiB(vmwareSizeCluster.memory.totalMB) / vmwareSizeCluster.hostCount);
                    setVmcHostStorage(Math.round(convertMBToGB(vmwareSizeCluster.storage.totalCapacityMB) / vmwareSizeCluster.hostCount));
                } else {
                    setHasVMCRecommendation(false);
                }
            }

            const serverResult = awsEC2result.servers.find(ec2 => ec2.serverId === server.serverId);
            if (!serverResult) {
                return;
            }
            setAwsEC2result(awsEC2result);
            setServerResult(serverResult);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.ENCOUNTERED_ERROR_LOADING_APP_SERVER_DETAILS);
        } finally {
            setLoading(false);
        }
    }, [server, tcoAssessmentId, alert]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const [inScope, setInScope] = useState(server.inScope);
    const inScopeChange = async (event: NonCancelableCustomEvent<ToggleProps.ChangeDetail>) => {
        const { checked } = event.detail;
        setInScope(checked);
        try {
            await serverService.updateInScope(server.serverId, portfolioId, checked);
        } catch (err) {
            alert(FlashType.error, (err as Error).message, constants.ENCOUNTERED_ERROR_UPDATING_SERVER_INSCOPE);
            setInScope(!checked);
        }
    };

    const onPremHeader = (
        <Header variant='h2'>
            {constants.ON_PREMISES}
        </Header>
    );

    const storageSize = server.interpretedFields?.storageSizeGB.label;
    const storageMaxIops = server.interpretedFields?.storageMaxIops.label;
    const storageMaxThroughput = server.interpretedFields?.storageMaxThroughputMBPerSecond.label;
    const onPremPhysicalContainer = (
        <Container
            data-testid='onPremPhysicalContainer'
            header={onPremHeader}
        >
            <Grid
                gridDefinition={[{colspan: 12}, {colspan: 12}, {colspan: 12}, {colspan: 12}, {colspan: 12}]}
            >
                <ValueWithLabel label={constants.VIRTUALIZATION}>{constants.PHYSICAL}</ValueWithLabel>
                <ValueWithLabel label={constants.UPTIME}>{server.interpretedFields?.usagePct.label}</ValueWithLabel>
                <ValueWithLabel label={constants.OS}>{server.interpretedFields?.os.label}</ValueWithLabel>
                <ValueWithLabel label={constants.SPECS}>
                    {server.interpretedFields?.numCores.label} / {server.interpretedFields?.ramSize.label}
                </ValueWithLabel>
                <ValueWithLabel label={constants.STORAGE}>
                    {storageSize} / {storageMaxIops} / {storageMaxThroughput}
                </ValueWithLabel>
            </Grid>
        </Container>
    );

    const onPremVirtualContainer = (
        <Container
            data-testid='onPremVirtualContainer'
            header={onPremHeader}
        >
            <Grid
                gridDefinition={[{colspan: 6}, {colspan: 6}, {colspan: 12}, {colspan: 12}, {colspan: 12}, {colspan: 12}]}
            >
                <ValueWithLabel label={constants.VIRTUALIZATION}>Virtual</ValueWithLabel>
                <ValueWithLabel label={constants.HYPERVISOR}>{server.interpretedFields?.hypervisor?.label}</ValueWithLabel>
                <ValueWithLabel label={constants.UPTIME}>{server.interpretedFields?.usagePct.label}</ValueWithLabel>
                <ValueWithLabel label={constants.OS}>{server.interpretedFields?.os.label}</ValueWithLabel>
                <ValueWithLabel label={constants.SPECS}>
                    {server.interpretedFields?.numCores.label} / {server.interpretedFields?.ramSize.label}
                </ValueWithLabel>
                <ValueWithLabel label={constants.SPECS}>
                    {storageSize} / {storageMaxIops} / {storageMaxThroughput}
                </ValueWithLabel>
            </Grid>
        </Container>
    );

    const awsHeader = (
        <Header variant='h2'>
            {constants.IN_AWS}
        </Header>
    );

    const awsVMCRecommendations = hasVMCRecommendation && (
        <Grid
            data-testid='awsVMCRecommendations'
            gridDefinition={[{colspan: 12}, {colspan: 12}, {colspan: 12}, {colspan: 12}, {colspan: 12}]}
        >
            <ValueWithLabel label={constants.AWS_RECOMMENDATION}>{constants.VMWARE_TYPE}</ValueWithLabel>
            <ValueWithLabel label={constants.RECOMMENDED_PAYMENT_OPTION}>{vmcPaymentOption}</ValueWithLabel>
            <ValueWithLabel label={constants.OS}>{server.interpretedFields?.os.label}</ValueWithLabel>
            <ValueWithLabel label={constants.CLUSTER_NAME}>{vmcClusterName}</ValueWithLabel>
            <ValueWithLabel label={constants.HOST_TYPE}>
                {`${vmcHostType} / ${vmcHostvCPU} ${constants.V_CPUS} / ${vmcHostRAM} GiB`
                + ` ${constants.RAM} / ${constants.STORAGE_CAPITALIZED} ${vmcHostStorage} GB`}
            </ValueWithLabel>
        </Grid>
    );

    const awsVirtualRecommendations = hasVMCRecommendation ? awsVMCRecommendations : 
        (
            <AwsEC2RecommendationDetail
                tcoAssessmentId={tcoAssessmentId}
                awsEC2result={awsEC2result}
                server={serverResult}
            />
        );


    const getRecalculateTcoMessage = () => (
        <Alert header={constants.RECALCULATE_TCO_MESSAGE_HEADER}>
            {constants.RECALCULATE_TCO_MESSAGE}
        </Alert>
    );

    const awsPhysicalContainer = loading ? (
        <Container
            header={awsHeader}
        >
            <Spinner data-testid='loadingPhysicalAWS' size='large'/>
        </Container>
    ) : (
        <Container
            header={awsHeader}
        >
            {tcoAssessmentId ? (
                <AwsEC2RecommendationDetail
                    tcoAssessmentId={tcoAssessmentId}
                    awsEC2result={awsEC2result}
                    server={serverResult}
                />
            ) : getRecalculateTcoMessage() }
        </Container>
    );

    const awsVirtualContainer = loading ? (
        <Container
            header={awsHeader}
        >
            <Spinner data-testid='loadingVirtualAWS' size='large'/>
        </Container>
    ) : (
        <Container
            header={awsHeader}
        >
            {tcoAssessmentId ? awsVirtualRecommendations : getRecalculateTcoMessage()}
        </Container>
    );

    const infoLinkClick = () => onInfoLinkClick('applicationResourcesServerDetails');

    const serverDetailHeader = (
        <Grid gridDefinition={[{colspan: 12}, {colspan: 12}]} disableGutters>
            <SpaceBetween size='xs' data-testid='serverDetailHeader'>
                <SpaceBetween direction='horizontal' size='xs'>
                    <strong>{server.serverId}</strong>
                    <Link variant='info' onFollow={infoLinkClick}>Info</Link>
                </SpaceBetween>
                <Toggle
                    checked={inScope}
                    onChange={inScopeChange}
                >
                    {constants.IN_SCOPE_OF_PORTFOLIO}
                </Toggle>
            </SpaceBetween>
            <div>{constants.UTILIZATION_METHOD} {server.interpretedFields?.utilizationMethod.label}</div>
        </Grid>
    );

    const onPremVsAWSHeader = (
        <Header variant='h2'>
            {constants.ON_PREM_VS_AWS}
        </Header>
    );

    const serverDetailsPhysicalContent = (
        <Grid
            data-testid='serverPhysicalContent'
            id='serverDetails'
            gridDefinition={[{colspan: 12 }, {colspan: 12 }, {colspan: 12 }]}
        >
            {serverDetailHeader}
            {onPremVsAWSHeader}
            <Grid gridDefinition={[{colspan: 6}, {colspan: 6}]}>
                <div>{onPremPhysicalContainer}</div>
                <div>{awsPhysicalContainer}</div>
            </Grid>
        </Grid>
    );

    const serverDetailsVirtualContent = (
        <Grid
            data-testid='serverVirtualContent'
            id='serverDetails'
            gridDefinition={[{colspan: 12}, {colspan: 12}, {colspan: 12}]}
        >
            {serverDetailHeader}
            {onPremVsAWSHeader}
            <Grid gridDefinition={[{colspan: 6}, {colspan: 6}]}>
                <div>{onPremVirtualContainer}</div>
                <div>{awsVirtualContainer}</div>
            </Grid>
        </Grid>
    );

    const serverDetailsContent = server.interpretedFields?.isPhysical.value ? serverDetailsPhysicalContent : serverDetailsVirtualContent;

    return (
        <Container>
            {serverDetailsContent}
        </Container>
    );
}

export default withFlashContext(ApplicationResourceServerDetails);
